import { Download, ListPlus, RefreshCw, Upload, Users } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Toolbar } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogForm, DialogHeader,
  DialogTitle, DiscardGuard,
} from "@/components/ui/dialog";
import { Callout, EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { CallPager, CallRangeLabel } from "@/features/call-report";
import { nextReasonText, priorityReasonText } from "@/lib/cadence";
import { DIALING_BOUNDS } from "@/lib/campaign";
import { ApiError, fetchFile, saveBlob } from "@/lib/api";
import {
  LEAD_STATES, leadName, leadStateText, listFileProblem, listNameFromFile, listOutcome,
  listStatusText, nextTry, skipSummary, unreadSummary,
} from "@/lib/campaign";
import {
  LEADS_PAGE_SIZE, useCampaignLeads, useCampaignLists, useImportList, useRefetchCampaignLeads,
  useUpdateCampaign,
} from "@/lib/queries";
import type { Campaign, LeadList } from "@/lib/types";
import { formatDateTime, formatPhoneNumber } from "@/lib/utils";

/**
 * The campaign's dial lists, and the leads on them (ADR-019).
 *
 * An import is two steps, and the screen keeps them apart. The upload answers once the file has
 * been read and counted; the import runs after it and writes what it made of the file on the list
 * when it finishes. So a new list says Importing, the lists poll while one is, and the leads
 * refetch the moment it is done.
 *
 * Consent is not asked for (ACL-575, platform ADR-194): core-api writes the fixed evidence on each
 * new lead, or keeps a row's own consent date when the file has one, and skips no row for it.
 */
export function CampaignLists({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const lists = useCampaignLists(campaign.id);
  const refetchLeads = useRefetchCampaignLeads(campaign.id);
  const [importing, setImporting] = React.useState(false);

  const running = lists.data?.some((list) => list.status === "importing") ?? false;
  const wasRunning = React.useRef(running);
  React.useEffect(() => {
    if (wasRunning.current && !running) refetchLeads();
    wasRunning.current = running;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const importButton = canManage && (
    <Button variant="outline" size="sm" onClick={() => setImporting(true)}>
      <Upload />
      Import a list
    </Button>
  );

  return (
    <div className="space-y-4">
      <ListHorizon campaign={campaign} canManage={canManage} />
      <Card className="overflow-hidden">
        <CardHeader
          title="Lists"
          description="Each file of leads imported into the campaign, and what the import made of it."
          action={importButton}
        />
        {lists.isPending ? (
          <TableSkeleton rows={2} cols={4} />
        ) : lists.error ? (
          <ErrorState
            error={lists.error}
            title="Could not load the lists"
            action={
              <Button variant="outline" onClick={() => void lists.refetch()}>
                Try again
              </Button>
            }
          />
        ) : lists.data.length === 0 ? (
          <EmptyState
            icon={ListPlus}
            title="No lists yet"
            description="Import a CSV of leads, and the dialer has someone to call."
            action={importButton || undefined}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>List</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>What it made</TableHead>
                <TableHead>Uploaded</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lists.data.map((list) => {
                const status = listStatusText(list.status);
                const skips = skipSummary(list.skip_reasons ?? {});
                const unread = unreadSummary(list.warnings ?? {});
                return (
                  <TableRow key={list.id}>
                    <TableCell className="font-medium text-text">{list.name}</TableCell>
                    <TableCell>
                      <Badge tone={status.tone} size="sm">
                        {list.status === "importing" && <Spinner className="size-2.5" />}
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      <span className={list.status === "failed" ? "text-danger-11" : "text-text"}>
                        {listOutcome(list)}
                      </span>
                      {skips && <span className="block text-text-muted">Skipped for {skips}</span>}
                      {unread && (
                        <span
                          className="block text-warning-11"
                          title="These leads were imported without it. Set it on each contact."
                        >
                          Could not read: {unread}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-text-muted">{formatDateTime(list.created_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <LeadsCard campaign={campaign} lists={lists.data ?? []} />

      <ImportListDialog open={importing} onOpenChange={setImporting} campaign={campaign} />
    </div>
  );
}

const ALL = "all";

function LeadsCard({ campaign, lists }: { campaign: Campaign; lists: LeadList[] }) {
  const [state, setState] = React.useState(ALL);
  const [listId, setListId] = React.useState(ALL);
  const [page, setPage] = React.useState(0);
  const filters = {
    state: state === ALL ? undefined : state,
    list_id: listId === ALL ? undefined : Number(listId),
  };
  const { data, isPending, isFetching, error, refetch } = useCampaignLeads(campaign.id, filters, page);
  const listNames = new Map(lists.map((list) => [list.id, list.name]));
  const filtered = state !== ALL || listId !== ALL;

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Leads"
        description="Everyone the lists brought in, and where each one stands with the dialer."
        action={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Refresh the leads"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            <RefreshCw />
          </Button>
        }
      />
      <Toolbar>
        <Select
          value={state}
          onValueChange={(next) => {
            setState(next);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-48" aria-label="Where the lead stands">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Every state</SelectItem>
            {LEAD_STATES.map((s) => (
              <SelectItem key={s} value={s} description={leadStateText(s).description}>
                {leadStateText(s).label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {lists.length > 0 && (
          <Select
            value={listId}
            onValueChange={(next) => {
              setListId(next);
              setPage(0);
            }}
          >
            <SelectTrigger className="w-56" aria-label="List">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Every list</SelectItem>
              {lists.map((list) => (
                <SelectItem key={list.id} value={String(list.id)}>
                  {list.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <span className="ml-auto text-xs text-text-muted">
          {data && (
            <CallRangeLabel from={page * LEADS_PAGE_SIZE} count={data.items.length} total={data.total} />
          )}
        </span>
      </Toolbar>

      {isPending ? (
        <TableSkeleton cols={6} />
      ) : error ? (
        <ErrorState
          error={error}
          title="Could not load the leads"
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      ) : data.items.length === 0 ? (
        <EmptyState
          icon={Users}
          title={filtered ? "No leads match" : "No leads yet"}
          description={filtered ? "Try another state or list." : "Import a list, and its leads appear here."}
        />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lead</TableHead>
                <TableHead>Stands</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Last outcome</TableHead>
                <TableHead>Next try</TableHead>
                <TableHead>Why</TableHead>
                <TableHead className="text-right">Priority</TableHead>
                <TableHead>List</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((lead) => {
                const stands = leadStateText(lead.state);
                const name = leadName(lead);
                const next = nextTry(lead);
                return (
                  <TableRow key={lead.id}>
                    <TableCell>
                      <Link
                        to={`/contacts/${lead.lead_id}`}
                        className={name ? "block text-sm text-text hover:underline" : "block text-sm text-text-muted hover:underline"}
                      >
                        {name ?? "No name"}
                      </Link>
                      <span className="block font-mono text-xs text-text-muted">
                        {formatPhoneNumber(lead.phone)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge tone={stands.tone} size="sm" title={stands.description}>
                        {stands.label}
                      </Badge>
                      {lead.block_reason && (
                        <span className="mt-0.5 block text-xs text-text-muted">{lead.block_reason}</span>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">{lead.attempts}</TableCell>
                    <TableCell className="text-xs text-text">
                      {lead.last_disposition ?? lead.last_outcome ?? <span className="text-text-subtle">—</span>}
                    </TableCell>
                    <TableCell className="text-xs text-text-muted">
                      {next.at ? `${next.held ? "On hold until " : ""}${formatDateTime(next.at)}` : "—"}
                    </TableCell>
                    <TableCell className="text-xs text-text-muted">
                      {nextReasonText(lead.next_reason) ?? "—"}
                      {/* The row's counters are the cadence's once it has placed the row (a reason is
                          written with every decision); before that the Cadence tab derives a position
                          from the attempts, and two different numbers here would only confuse. */}
                      {campaign.cadence_id && lead.next_reason && lead.cadence_cycle !== undefined && (
                        <span className="block whitespace-nowrap text-text-subtle">
                          cycle {lead.cadence_cycle}, call {(lead.cadence_step ?? 0) + 1} next
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums text-text-muted">
                      {lead.priority}
                      {lead.priority_reason && (
                        <span className="block text-text-subtle">{priorityReasonText(lead.priority_reason)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-text-muted">
                      {lead.list_id === null ? "—" : (listNames.get(lead.list_id) ?? `List ${lead.list_id}`)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <CallPager
            page={page}
            count={data.items.length}
            total={data.total}
            onChange={setPage}
            busy={isFetching}
            pageSize={LEADS_PAGE_SIZE}
          />
        </>
      )}
    </Card>
  );
}

function ImportListDialog({
  open, onOpenChange, campaign,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}) {
  const toast = useToast();
  const importList = useImportList();
  const [file, setFile] = React.useState<File | null>(null);
  const [fileError, setFileError] = React.useState<string | null>(null);
  const [name, setName] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);
  const [downloading, setDownloading] = React.useState(false);

  /** The header row the importer actually reads, as a file. Typing it by hand is how an import
   *  loses rows, and the skipped count is the only place that shows up. */
  async function downloadTemplate() {
    setDownloading(true);
    try {
      const { blob } = await fetchFile("/lists/import-template.csv");
      saveBlob(blob, "lead-import-template.csv");
    } catch (error) {
      toast.error(
        "Could not download the template",
        error instanceof ApiError ? error.message : "Try again in a moment.",
      );
    } finally {
      setDownloading(false);
    }
  }

  React.useEffect(() => {
    if (!open) return;
    setFile(null);
    setFileError(null);
    setName("");
    setFormError(null);
  }, [open]);

  const choose = (next: File | null) => {
    setFile(next);
    setFileError(next ? listFileProblem(next) : null);
    if (next && !name.trim()) setName(listNameFromFile(next.name));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!file) {
      setFileError("Choose a CSV file.");
      return;
    }
    const problem = listFileProblem(file);
    if (problem) {
      setFileError(problem);
      return;
    }
    if (!name.trim()) {
      setFormError("A list needs a name.");
      return;
    }
    try {
      const list = await importList.mutateAsync({
        campaignId: campaign.id,
        file,
        name: name.trim(),
      });
      toast.success("Import started", `${list.total_rows.toLocaleString()} rows from ${list.name}`);
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not import the list.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={file !== null || name.trim() !== ""} />
        <DialogHeader>
          <DialogTitle>Import a list</DialogTitle>
          <DialogDescription>
            A CSV with a header row and a phone column. Each row becomes a lead on {campaign.name}.
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <Field
              label="File"
              htmlFor="list-file"
              required
              error={fileError ?? undefined}
              hint="Up to 5 MB and 50,000 rows. Download the template for the columns, filled in with one example row."
            >
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => choose(e.target.files?.[0] ?? null)}
                className="block w-full text-xs text-text-muted file:mr-3 file:rounded-md file:border file:border-border file:bg-surface file:px-2.5 file:py-1.5 file:text-xs file:font-medium file:text-text hover:file:bg-surface-hover"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2 -ml-2"
                onClick={() => void downloadTemplate()}
                disabled={downloading}
              >
                <Download className="size-3.5" />
                Download template
              </Button>
            </Field>

            <Field label="Name" htmlFor="list-name" required>
              <Input
                autoComplete="off"
                maxLength={200}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={importList.isPending}>
              Import list
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}


/**
 * How long a lead stays on this campaign's lists.
 *
 * It sat on the Dialing tab, which read as a dialer setting and is not one: an inbound campaign
 * has lists too — they are what `lookup.py` identifies its callers from, and what a disposition
 * on an inbound call moves — so hiding the dialer from such a campaign would have taken this
 * with it. It belongs beside the lists it governs.
 */
function ListHorizon({ campaign, canManage }: { campaign: Campaign; canManage: boolean }) {
  const update = useUpdateCampaign();
  const toast = useToast();
  const [days, setDays] = React.useState(String(campaign.list_horizon_days));
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setDays(String(campaign.list_horizon_days));
  }, [campaign.list_horizon_days]);

  const [min, max] = DIALING_BOUNDS.list_horizon_days;
  const value = Number(days);
  const problem =
    days.trim() === "" || !Number.isInteger(value) || value < min || value > max
      ? `A whole number of days, ${min} to ${max.toLocaleString()}`
      : null;
  const changed = value !== campaign.list_horizon_days;

  const save = async () => {
    setError(null);
    if (problem) return;
    try {
      await update.mutateAsync({ id: campaign.id, list_horizon_days: value });
      toast.success("Saved", campaign.name);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save it.");
    }
  };

  return (
    <Card>
      <CardHeader
        title="How long leads stay"
        description="A lead leaves this campaign's lists when its time is up. It stops being dialed, and stops being the answer when somebody with that number calls in."
      />
      <div className="px-4 pb-4">
        {error && <Callout tone="danger">{error}</Callout>}
        <fieldset disabled={!canManage} className="flex flex-wrap items-end gap-2">
          <Field
            label="Leads leave a list after"
            htmlFor="list-horizon"
            error={problem && changed ? problem : undefined}
            hint="Days after a lead joined the campaign. 0 keeps them for good."
            className="max-w-64"
          >
            <Input
              id="list-horizon"
              type="number"
              inputMode="numeric"
              invalid={Boolean(problem && changed)}
              value={days}
              onChange={(e) => setDays(e.target.value)}
            />
          </Field>
          {canManage && (
            <Button
              variant="outline"
              className="mb-1"
              loading={update.isPending}
              disabled={!changed || Boolean(problem)}
              onClick={save}
            >
              Save
            </Button>
          )}
        </fieldset>
      </div>
    </Card>
  );
}
